import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { parseEther } from "viem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { strikeExchangeAbi, strikeRouterAbi } from "../abi";
import {
  ApiError,
  orderTuple,
  requestQuote,
  useApiConfig,
  useCandles,
  useMarket,
  useOptionChain,
  useUserOrders,
  type ChainSeries,
  type SignedQuote,
} from "../lib/api";
import { PONS_URL } from "../lib/config";
import { fmtEth, fmtExpiry, fmtMcap, fmtNum, fmtPct, fmtUsd, timeLeft, wei } from "../lib/format";
import { byExpiry, expiryKind, fmtProb, probItm, shortDate } from "../lib/options";
import { publicClient, sendTx } from "../lib/tx";
import { useWallet } from "../lib/wallet";
import { demoFilled, isDemo } from "../lib/demo";
import { ConnectButton } from "../components/Connect";
import { PayoffChart } from "../components/PayoffChart";
import { PriceChart, type ChartLine } from "../components/PriceChart";
import { Countdown } from "../components/Countdown";
import "./trade.css";

const TFS = [
  [300, "5m"],
  [900, "15m"],
  [3600, "1h"],
  [14400, "4h"],
] as const;

const breakevenOf = (s: ChainSeries) => (s.isCall ? s.strikeEth + s.askEth : Math.max(0, s.strikeEth - s.askEth));

function useNarrow(px = 760) {
  const q = `(max-width: ${px}px)`;
  const [narrow, setNarrow] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [q]);
  return narrow;
}

export function Trade() {
  const { data: m } = useMarket();
  const { data: chain, isLoading } = useOptionChain();
  const { data: cfg } = useApiConfig();
  const [tf, setTf] = useState(900);
  const { data: candles } = useCandles(tf);
  const expiries = useMemo(() => byExpiry(chain?.series ?? []), [chain]);
  const expiryList = [...expiries.keys()];
  const [expiry, setExpiry] = useState<number>();
  const [params] = useSearchParams();
  const [sel, setSel] = useState<string | undefined>(() => params.get("s") ?? undefined);
  const [allStrikes, setAllStrikes] = useState(false);
  const narrow = useNarrow();
  const [side, setSide] = useState<"call" | "put">("call");
  const selExpiry = chain?.series.find((s) => s.id === sel)?.expiry;
  // Default to the first expiry with most of a day left; same-day dailies are one tap away.
  const firstUseful = expiryList.find((e) => e - Date.now() / 1000 > 20 * 3600) ?? expiryList[0];
  const activeExpiry = expiry && expiries.has(expiry) ? expiry : (selExpiry ?? firstUseful);
  const spot = m?.priceEth ?? 0;
  const eu = m?.ethUsd ?? 0;
  const sigma = chain?.sigma ?? 1.5;
  const allRows = activeExpiry ? expiries.get(activeExpiry)! : [];
  // Near the money by default: strikes between 0.45x and 3.2x of spot.
  const nearRows = allRows.filter((r) => spot === 0 || (r.strikeEth >= spot * 0.45 && r.strikeEth <= spot * 3.2));
  const rows = allStrikes || nearRows.length === 0 ? allRows : nearRows;
  const hidden = allRows.length - nearRows.length;
  const selected = rows.find((s) => s.id === sel) ?? pickDefault(rows);

  // On phones the ticket sits under the chain: bring it into view after a pick.
  const pick = (id: string) => {
    setSel(id);
    if (window.matchMedia("(max-width: 1040px)").matches) {
      requestAnimationFrame(() => document.querySelector(".trade-side")?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  return (
    <section className="wrap trade">
      <header className="mkt">
        <div className="mkt-id">
          <img src="/mark.svg" alt="" width="40" height="40" />
          <div>
            <div className="mkt-pair mono">$STRIKE / ETH · options</div>
            <div className="mkt-cap">
              <span className="mkt-v num">{m?.launched ? fmtUsd(m.marketCapUsd) : "—"}</span>
              <span className="mkt-unit">mcap</span>
              {m && <span className={`mkt-chg num ${m.change24hPct >= 0 ? "up" : "down"}`}>{fmtPct(m.change24hPct)}</span>}
            </div>
          </div>
        </div>
        <div className="mkt-stats">
          <MktStat label="Price" value={m ? fmtUsd(m.priceUsd) : "—"} />
          <MktStat label="24h volume" value={m ? fmtEth(m.volume24hEth) : "—"} />
          <MktStat label="Implied vol" value={chain?.sigma ? `${Math.round(chain.sigma * 100)}%` : "—"} />
          <MktStat label="Next expiry" value={expiryList[0] ? <Countdown to={expiryList[0]} /> : "—"} />
        </div>
      </header>

      <div className="trade-grid">
        <div className="trade-main">
          <div className="panel chart-panel">
            <div className="panel-head">
              <h3>Market cap</h3>
              <div className="tabs tabs-sm">
                {TFS.map(([v, l]) => (
                  <button key={v} className={tf === v ? "on" : ""} onClick={() => setTf(v)}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {candles && candles.length > 0 ? (
              <div className="chart-body">
                <PriceChart candles={candles} ethUsd={eu} height={narrow ? 240 : 320} lines={selectedLines(selected)} />
              </div>
            ) : (
              <div className="empty" style={{ height: 320 }}>
                No trades yet.
              </div>
            )}
          </div>

          <div className="panel chain-panel">
            <div className="chain-top">
              <div>
                <h3>Option chain</h3>
                <p className="chain-sub mono">
                  {activeExpiry ? (
                    <>
                      Expires {fmtExpiry(activeExpiry)} · in <Countdown to={activeExpiry} />
                    </>
                  ) : (
                    "Daily, weekly and monthly expiries, 20:00 UTC"
                  )}
                </p>
              </div>
              <div className="chain-controls">
                {narrow && (
                  <div className={`tabs tabs-sm ${side}`}>
                    <button className={side === "call" ? "on" : ""} onClick={() => setSide("call")}>
                      Calls
                    </button>
                    <button className={side === "put" ? "on" : ""} onClick={() => setSide("put")}>
                      Puts
                    </button>
                  </div>
                )}
                <div className="expiries">
                  {expiryList.map((e) => (
                    <button key={e} className={e === activeExpiry ? "on" : ""} onClick={() => (setExpiry(e), setSel(undefined))}>
                      <span>{shortDate(e)}</span>
                      <span className="mono">
                        {timeLeft(e).split(" ")[0]}
                        {expiryKind(e) !== "daily" && ` · ${expiryKind(e)}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {!chain?.open ? (
              <ClosedState phase={m?.phase} progress={m?.graduation?.progress} token={cfg?.contracts.token ?? undefined} />
            ) : isLoading ? (
              <div className="empty">Loading the chain…</div>
            ) : (
              <>
                <ChainTable
                  rows={rows}
                  spot={spot}
                  ethUsd={eu}
                  sigma={sigma}
                  selected={selected?.id}
                  onPick={pick}
                  only={narrow ? side : undefined}
                />
                <div className="chain-foot">
                  <span className="mono faint">Prices per 1M options, in ETH. Prob. is the model's chance of finishing in the money.</span>
                  {hidden > 0 && (
                    <button className="chain-more mono" onClick={() => setAllStrikes((v) => !v)}>
                      {allStrikes ? "Near the price only" : `Show ${hidden} far strikes`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <aside className="trade-side">
          {selected && chain?.open ? <Ticket s={selected} spotEth={spot} ethUsd={eu} sigma={sigma} /> : <TicketIdle />}
        </aside>
      </div>
    </section>
  );
}

/** The picked option on the chart: where it starts paying, and where it pays back its premium. */
function selectedLines(s?: ChainSeries): ChartLine[] {
  if (!s) return [];
  const color = s.isCall ? "#c6ff3d" : "#ff4d6d";
  const be = breakevenOf(s);
  const lines: ChartLine[] = [{ priceEth: s.strikeEth, color, title: s.isCall ? "CALL STRIKE" : "PUT STRIKE" }];
  // Labels would overlap when the premium is tiny next to the strike.
  if (Math.abs(be / s.strikeEth - 1) > 0.03) lines.push({ priceEth: be, color: "#8a8d93", title: "BREAKEVEN", style: "dotted" });
  return lines;
}

function MktStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="mkt-stat">
      <span className="eyebrow">{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

function pickDefault(rows: ChainSeries[]): ChainSeries | undefined {
  const calls = rows.filter((r) => r.isCall).sort((a, b) => Math.abs(a.moneyness - 1.25) - Math.abs(b.moneyness - 1.25));
  return calls[0] ?? rows[0];
}

function ClosedState({ phase, progress, token }: { phase?: number; progress?: number; token?: string }) {
  return (
    <div className="empty">
      <span className="eyebrow">{phase === undefined || phase < 0 ? "Pre-launch" : "Bonding curve"}</span>
      <h3 style={{ color: "var(--text)", fontSize: 22 }}>Options open when $STRIKE graduates.</h3>
      <p style={{ maxWidth: 440 }}>
        Once the pons curve fills, liquidity moves to a locked Uniswap pool and the option chain goes live.
        {progress !== undefined && ` The curve is ${Math.round(progress * 100)}% full.`}
      </p>
      {token && (
        <a className="btn btn-primary" href={`${PONS_URL}/${token}`} target="_blank" rel="noreferrer">
          Buy $STRIKE on pons
        </a>
      )}
    </div>
  );
}

type ChainProps = {
  rows: ChainSeries[];
  spot: number;
  ethUsd: number;
  sigma: number;
  selected?: string;
  onPick: (id: string) => void;
  only?: "call" | "put";
};

/** Calls on the left, puts on the right, mirrored around the strike; a marker row shows where the price is now. */
function ChainTable({ rows, spot, ethUsd, sigma, selected, onPick, only }: ChainProps) {
  const strikes = [...new Set(rows.map((r) => r.strikeEth))].sort((a, b) => a - b);
  const byKey = new Map(rows.map((r) => [`${r.isCall ? "c" : "p"}:${r.strikeEth}`, r]));
  const spotAt = strikes.findIndex((k) => k > spot);

  const price = (r: ChainSeries | undefined, isCall: boolean) =>
    r ? (
      <button className={`cc-price ${isCall ? "c" : "p"} ${selected === r.id ? "on" : ""}`} onClick={() => onPick(r.id)}>
        <span className="cc-eth num">{fmtEth(r.askEth * 1e6, 4).replace(" ETH", "")}</span>
        <span className="cc-usd num">{fmtUsd(r.askEth * 1e6 * ethUsd)}</span>
      </button>
    ) : (
      <span className="cc-none">—</span>
    );
  const be = (r?: ChainSeries) => <span className="cc-meta num">{r ? fmtMcap(breakevenOf(r), ethUsd) : ""}</span>;
  const pr = (r?: ChainSeries) => <span className="cc-meta num">{r ? fmtProb(probItm(r, spot, sigma)) : ""}</span>;

  const strikeCell = (k: number) => (
    <span className="cc-strike">
      <span className="cc-k num">{fmtMcap(k, ethUsd)}</span>
      <span className="cc-dist num">
        {k > spot ? "+" : ""}
        {Math.round((k / spot - 1) * 100)}%
      </span>
    </span>
  );

  const spotRow = (
    <div className="cc-spot" role="row">
      <span className="num">
        {fmtMcap(spot, ethUsd)} <span>price now</span>
      </span>
    </div>
  );

  if (only) {
    const isCall = only === "call";
    return (
      <div className={`cc cc-one ${only}`} role="table">
        <div className="cc-head" role="row">
          <span>Strike</span>
          <span>Breakeven</span>
          <span>Prob.</span>
          <span>Price</span>
        </div>
        {strikes.map((k, i) => {
          const r = byKey.get(`${isCall ? "c" : "p"}:${k}`);
          const itm = isCall ? k < spot : k > spot;
          return (
            <div key={k}>
              {i === spotAt && spotRow}
              <div className={`cc-row ${itm ? "itm" : ""} ${r && selected === r.id ? "sel" : ""}`} role="row">
                {strikeCell(k)}
                {be(r)}
                {pr(r)}
                {price(r, isCall)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="cc cc-both" role="table">
      <div className="cc-head" role="row">
        <span className="call">Breakeven</span>
        <span className="call">Prob.</span>
        <span className="call">Call price</span>
        <span>Strike</span>
        <span className="put">Put price</span>
        <span className="put">Prob.</span>
        <span className="put">Breakeven</span>
      </div>
      {strikes.map((k, i) => {
        const c = byKey.get(`c:${k}`);
        const p = byKey.get(`p:${k}`);
        const sel = (c && selected === c.id) || (p && selected === p.id);
        return (
          <div key={k}>
            {i === spotAt && spotRow}
            <div className={`cc-row ${sel ? "sel" : ""}`} role="row">
              <div className={`cc-side c ${k < spot ? "itm" : ""}`}>
                {be(c)}
                {pr(c)}
                {price(c, true)}
              </div>
              {strikeCell(k)}
              <div className={`cc-side p ${k > spot ? "itm" : ""}`}>
                {price(p, false)}
                {pr(p)}
                {be(p)}
              </div>
            </div>
          </div>
        );
      })}
      {spotAt === -1 && strikes.length > 0 && spotRow}
    </div>
  );
}

function TicketIdle() {
  return (
    <div className="panel ticket">
      <div className="empty">
        <span className="eyebrow">Ticket</span>
        <p>Pick a call or a put in the chain.</p>
      </div>
    </div>
  );
}

const SIZES = [100_000, 1_000_000, 5_000_000, 10_000_000];

function Ticket({ s, spotEth, ethUsd, sigma }: { s: ChainSeries; spotEth: number; ethUsd: number; sigma: number }) {
  const conn = useWallet();
  const { data: cfg } = useApiConfig();
  const qc = useQueryClient();
  const [size, setSize] = useState("1000000");
  const [quote, setQuote] = useState<SignedQuote>();
  const [qErr, setQErr] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const amount = useMemo(() => {
    const n = Number(size.replace(/,/g, ""));
    return Number.isFinite(n) && n > 0 ? parseEther(String(Math.floor(n))) : 0n;
  }, [size]);
  const capacity = wei(s.vaultCapacity);
  const color = s.isCall ? "call" : "put";

  // Fetch a signed quote whenever the series or size changes, and again when it expires.
  useEffect(() => {
    setQuote(undefined);
    setQErr(undefined);
    if (amount === 0n) return;
    const t = setTimeout(async () => {
      try {
        setQuote(await requestQuote(s.id, amount));
      } catch (e) {
        setQErr(e instanceof ApiError ? e.message : "Quotes are unavailable right now.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [s.id, amount, tick]);

  useEffect(() => {
    if (!quote) return;
    const ms = (quote.expiresAt - 5) * 1000 - Date.now();
    const t = setTimeout(() => setTick((x) => x + 1), Math.max(1_000, ms));
    return () => clearTimeout(t);
  }, [quote]);

  const n = Number(size || 0);
  const premiumEth = quote ? wei(quote.premium) : s.askEth * n;
  const perOption = quote ? quote.askEthPerOption : s.askEth;
  const be = s.isCall ? s.strikeEth + perOption : s.strikeEth - perOption;
  const ifMove = Math.max(0, s.isCall ? spotEth * 2 - s.strikeEth : s.strikeEth - spotEth / 2) * n - premiumEth;

  async function buy() {
    if (!quote || !cfg) return;
    setBusy(true);
    try {
      const premium = BigInt(quote.premium);
      await sendTx(`Buy ${s.isCall ? "calls" : "puts"}`, {
        address: cfg.contracts.router,
        abi: strikeRouterAbi,
        functionName: "buyWithEth",
        args: [orderTuple(quote.order), quote.signature, amount],
        value: premium + premium / 200n + 1n, // tiny buffer; the router refunds the rest
      });
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["chain"] });
      setTick((x) => x + 1);
    } catch {
      setTick((x) => x + 1); // refresh the quote after a failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`panel ticket tone-${color}`}>
      <div className="ticket-head">
        <span className={`ticket-kind ${color}`}>{s.isCall ? "Call" : "Put"}</span>
        <span className="mono mute">
          {shortDate(s.expiry)} · {timeLeft(s.expiry)}
        </span>
      </div>
      <h2 className="ticket-title">
        Pays if $STRIKE is {s.isCall ? "above" : "below"}
        <span className={`ticket-k num ${color}`}>{fmtMcap(s.strikeEth, ethUsd)}</span>
      </h2>
      <p className="note">market cap at expiry, {fmtExpiry(s.expiry)}</p>

      <div className="field" style={{ marginTop: 20 }}>
        <label>
          <span>Options (1 option = 1 $STRIKE)</span>
          <span>max {fmtNum(capacity)}</span>
        </label>
        <div className="input">
          <input inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value.replace(/[^\d]/g, ""))} aria-label="Number of options" />
          <span className="unit">options</span>
        </div>
        <div className="chips">
          {SIZES.map((x) => (
            <button key={x} className={n === x ? "chip-on" : ""} onClick={() => setSize(String(x))}>
              {fmtNum(x)}
            </button>
          ))}
          <button onClick={() => setSize(String(Math.floor(capacity)))}>Max</button>
        </div>
      </div>

      <div className="ticket-pay">
        <span className="eyebrow">You pay</span>
        <span className={`ticket-pay-v num ${quote ? "" : "faint"}`}>{fmtEth(premiumEth)}</span>
        <span className="mono mute">≈ {fmtUsd(premiumEth * ethUsd)} · the most you can lose</span>
      </div>

      <div className="ticket-box">
        <div className="kv">
          <span>Breakeven</span>
          <span>{fmtMcap(Math.max(0, be), ethUsd)}</span>
        </div>
        <div className="kv">
          <span>Chance in the money</span>
          <span>{fmtProb(probItm(s, spotEth, sigma))}</span>
        </div>
        <div className="kv">
          <span>If it {s.isCall ? "2x's" : "halves"} by expiry</span>
          <span className={ifMove > 0 ? color : "mute"}>
            {ifMove > 0 ? "+" : ""}
            {fmtEth(ifMove)}
          </span>
        </div>
      </div>

      <div className="ticket-chart">
        <PayoffChart isCall={s.isCall} strike={s.strikeEth} premium={perOption} spot={spotEth} height={120} labelFor={(p) => fmtMcap(p, ethUsd)} />
      </div>

      {capacity === 0 ? (
        <p className="note warn">
          {s.isCall
            ? "The vault has sold all it can on this strike for now. Try another strike, or buy from holders below."
            : "The vault starts selling puts once it has earned ETH from premiums. Puts offered by holders are below."}
        </p>
      ) : (
        qErr && <p className="note warn">{qErr}</p>
      )}

      <div style={{ marginTop: 14 }}>
        {conn.isConnected ? (
          <button className={`btn btn-lg btn-block ${s.isCall ? "btn-primary" : "btn-put"}`} disabled={!quote || busy} onClick={buy}>
            {busy ? "Confirm in wallet…" : `Buy ${fmtNum(n)} ${s.isCall ? "calls" : "puts"}`}
          </button>
        ) : (
          <ConnectButton block />
        )}
      </div>
      <p className="note ticket-foot">
        {quote ? (
          <>
            Quote refreshes in <Countdown to={quote.expiresAt} />. 10% of the premium is the protocol fee.
          </>
        ) : (
          "Getting a quote…"
        )}{" "}
        Cash out any time from <Link to="/portfolio" className="call">Portfolio</Link>.
      </p>
      <CommunityAsks s={s} size={amount} />
    </div>
  );
}

function CommunityAsks({ s, size }: { s: ChainSeries; size: bigint }) {
  const { data } = useUserOrders(s.id);
  const { data: cfg } = useApiConfig();
  const conn = useWallet();
  const qc = useQueryClient();
  const asks = (data ?? []).filter((o) => o.side === 0 && o.premium_token.toLowerCase() === cfg?.contracts.weth.toLowerCase());
  const filled = useQuery({
    queryKey: ["filled", asks.map((a) => a.digest).join()],
    enabled: !!cfg && asks.length > 0,
    refetchInterval: 15_000,
    queryFn: () =>
      isDemo()
        ? demoFilled(asks.map((a) => a.digest))
        : Promise.all(
            asks.map((a) =>
              publicClient.readContract({ address: cfg!.contracts.exchange, abi: strikeExchangeAbi, functionName: "filled", args: [a.digest] }),
            ),
          ),
  });
  const open = asks
    .map((a, i) => ({ ...a, remaining: BigInt(a.amount) - (filled.data?.[i] ?? 0n) }))
    .filter((a) => a.remaining > 0n)
    .sort((a, b) => Number(BigInt(a.price) - BigInt(b.price)));
  if (!open.length) return null;

  async function take(o: (typeof open)[number]) {
    if (!cfg) return;
    const amt = size > 0n && size < o.remaining ? size : o.remaining;
    const premium = (amt * BigInt(o.price) + 10n ** 18n - 1n) / 10n ** 18n;
    try {
      await sendTx(`Buy ${fmtNum(wei(amt))} from a holder`, {
        address: cfg.contracts.router,
        abi: strikeRouterAbi,
        functionName: "buyWithEth",
        args: [
          {
            maker: o.maker,
            taker: "0x0000000000000000000000000000000000000000",
            seriesId: BigInt(o.series_id),
            side: o.side,
            amount: BigInt(o.amount),
            price: BigInt(o.price),
            premiumToken: o.premium_token,
            minSpot: BigInt(o.min_spot),
            maxSpot: BigInt(o.max_spot),
            deadline: BigInt(o.deadline),
            nonce: BigInt(o.nonce),
          },
          o.signature,
          amt,
        ],
        value: premium,
      });
      qc.invalidateQueries({ queryKey: ["positions"] });
      filled.refetch();
    } catch {
      /* reported by sendTx */
    }
  }

  return (
    <div className="asks">
      <span className="eyebrow">Also offered by holders</span>
      {open.slice(0, 5).map((o) => (
        <div key={o.digest} className="ask-row">
          <span className="mono">
            {fmtNum(wei(o.remaining))} <span className="faint">at</span> {fmtEth(wei(o.price) * 1_000_000)}
            <span className="faint"> / 1M</span>
          </span>
          {conn.isConnected && (
            <button className="btn" onClick={() => take(o)}>
              Buy
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
